import { useHttp } from "../http/useHttp";
import { useCallback, useMemo } from "react";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { paths } from "../generated-types";

type InvoicesQueryParams =
  paths["/dashboard/contracts/{contract_shared_uuid}/invoices"]["post"]["requestBody"]["content"]["application/json"];

export type GetFulcrumReportInput = {
  path: string;
} & Partial<InvoicesQueryParams>;

export interface UseReportOptions {
  filename: string;
  input: GetFulcrumReportInput;
  options?: UseAxiosQueryOptions<Blob>;
}

export const useReport = ({ filename, input, options }: UseReportOptions) => {
  const { client } = useHttp();
  const { error, isPending, isFetching, data, refetch } = useAxiosQuery<Blob>({
    queryKey: ["reports", input],
    queryFn: () => {
      const { path, ...options } = input;
      return client.post(path, options, { responseType: "blob" });
    },
    gcTime: 0,
    retry: 0,
    ...options,
  });

  const blob = useMemo(() => (data ? data.data : null), [data]);

  const download = useCallback(() => {
    if (!input.path) {
      return;
    }

    if (!blob) {
      refetch({ throwOnError: true }).then(() => download());
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  }, [blob, filename, input.path, refetch]);

  return { error, isPending, isFetching, download, blob };
};

export type UseReportReturn = ReturnType<typeof useReport>;
